<template>
    <div>
        <CCol sm="12">
            <CCard>
                <CCardBody class="d-flex flex-column">
                    <div class="d-flex flex-column">
                        <!-- Name -->
                        <div role="group" class="form-group col-12">
                            <label class=""> {{ $t('nameMandatory') }}</label>
                            <CInput type="text" v-model="character.name" :invalid-feedback="errorOutput['Name']" :is-valid="genericValidator(character.name, attemptedSubmit) && errorValidator('Name')"></CInput>
                        </div>
                        <!-- ObjFile -->
                        <div role="group" class="form-group col-12">
                            <CustomPicker v-model="character.objFile" :title="$t('objFile')" v-bind:class="{'is-invalid': genericValidator(character.objFile, attemptedSubmit) === false || errorValidator('ObjFile') }" :invalid-feedback="errorOutput['ObjFile']" mime="model/obj" accept=".obj"></CustomPicker>
                        </div>
                        <!-- MtlFile -->
                        <div role="group" class="form-group col-12">
                            <CustomPicker v-model="character.mtlFile" :title="$t('mtlFile')" v-bind:class="{'is-invalid': genericValidator(character.mtlFile, attemptedSubmit) === false || errorValidator('MtlFile') }" :invalid-feedback="errorOutput['MtlFile']" mime="model/mtl" accept=".mtl"></CustomPicker>
                        </div>
                        <!-- Image Texture -->
                        <div role="group" class="form-group col-12">
                            <CustomPicker v-model="character.image" :title="$t('imageTexture')" v-bind:class="{'is-invalid': genericValidator(character.image, attemptedSubmit) === false || errorValidator('Image') }" :invalid-feedback="errorOutput['Image']" mime="image/png" accept=".png"></CustomPicker>
                        </div>
                        <!-- Preview Image -->
                        <div role="group" class="form-group col-12 col-md-4">
                            <label class=""> {{ $t('previewImage') }}</label>
                            <GalleryPicker class="gallery-picker" v-model="character.previewImage" :ratioWidth="'1'" :ratioHeight="'1'" v-bind:class="{'is-invalid': genericValidator(character.previewImage, attemptedSubmit) === false || errorValidator('PreviewImage') === false }" :max="1"></GalleryPicker>
                            <div v-if="errorValidator('PreviewImage') === false" class="invalid-feedback d-block">{{ errorOutput['PreviewImage'] }}</div>
                        </div>
                    </div>
                    <!-- API errors that belong to no field above -->
                    <FormApiErrors :fields="['Image', 'MtlFile', 'Name', 'ObjFile', 'PreviewImage']"></FormApiErrors>
                    <!-- Action button + submitting -->  
                    <!-- Remove v-if to activate edit -->
                    <div role="group" class="form-group col-12 d-flex justify-content-end">
                        <CSpinner v-if="submitting" variant="success" small class="align-self-end"></CSpinner>
                        <CButton v-else type="button" class="btn btn-primary" @click="submitClicked">{{ $t('acceptChanges') }}</CButton>
                    </div>
                    <!-- Loading -->
                    <FormLoading v-if="loading"></FormLoading>
                </CCardBody>
            </CCard>
        </CCol>
        <ConfirmationPopUp :confirmationMessage="confirmationMessage" @closeConfirmRequested="onClickConfirmationPopup" v-model="showConfirmationPopup"></ConfirmationPopUp>
    </div>
    </template>
     
    <script>
import FormApiErrors from '@/controls/FormApiErrors.vue';
    import { genericValidator, errorValidator, deepClone } from "@/helpers/utils.js";
    import Character from "@/classes/Character";
    import { mapState, mapActions } from 'vuex'
    import LanguageDropdown from "@/controls/LanguageDropdown.vue";
    import { CONSTANTS } from '@/helpers/consts';
    import ConfirmationPopUp from "@/controls/popups/ConfirmationPopUp.vue";
    import FormLoading from '@/controls/FormLoading.vue'
    import CustomPicker from '@/controls/CustomPicker.vue'
    import { API_HELPER } from "@/helpers/api.js";
    import GalleryPicker from '@/controls/GalleryPicker.vue'

      export default {
      components: { FormApiErrors, LanguageDropdown, ConfirmationPopUp, FormLoading, CustomPicker, GalleryPicker },
        props:{    
            baseCharacter:
            {
                type: Object,
                default: null,
            },
            loading:
            {
                type: Boolean,
                default: false,
            },
        },
        data()
        {
            return {
                attemptedSubmit: false,
                currentLanguage: CONSTANTS.supportedLanguages[0],
                character: new Character(),
                submitting: false,
                showConfirmationPopup: false,
                confirmationMessage: "",
            }
        },
        mounted: function()
        {
            this.resetCharacter();
        },
        computed:
        {
            ...mapState({ 
                alertType: state => state.alert.type,
                alertOutput: state => state.alert.output
            }),
            errorOutput: function()
            {
                var result = [];

                if(this.alertType == 'alert-danger' && this.alertOutput && Array.isArray(this.alertOutput))
                {
                    result = this.alertOutput.reduce(function(accum, currentVal) {
                    accum[currentVal.key] = currentVal.value;
                        return accum;
                    }, {});
                }

                // Validate required fields
                if(!this.character.name) { result['Name'] = this.$i18n.t("validateRequiredField"); }
                if(!this.character.mtlFile) { result['MtlFile'] = this.$i18n.t("validateRequiredField"); }
                if(!this.character.objFile) { result['ObjFile'] = this.$i18n.t("validateRequiredField"); }
                if(!this.character.image) { result['Image'] = this.$i18n.t("validateRequiredField"); }
                if(!this.character.previewImage) { result['PreviewImage'] = this.$i18n.t("validateRequiredField"); }

                return result;
            },
            ...mapState('characters', ['putCharacterOutput']),
            ...mapState('characters', ['postCharacterOutput']),
        },
        methods:{
            resetCharacter(){
                if(!this.baseCharacter)
                {
                    this.character = new Character();
                }
                else
                {
                    this.character = deepClone(new Character, this.baseCharacter); 
                    this.character.mtlFile = this.character.mtlFilePath;
                    this.character.objFile = this.character.objFilePath;
                    this.character.image = this.character.imagePath;  
                    this.character.previewImage = {url: API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, this.character.previewImagePath])};            
                }
            },
            submitClicked(){
                this.attemptedSubmit = true;
                
                // Emit event
                if(this.character.isValid())
                {
                    this.submitting = true;
                    const payload = {
                        ...this.character,
                        mtlFile: !this.character.mtlFile.data && !this.character.mtlFile.mime ? null : this.character.mtlFile,
                        objFile: !this.character.objFile.data && !this.character.objFile.mime ? null : this.character.objFile,
                        image: !this.character.image.data && !this.character.image.mime ? null : this.character.image,
                        previewImage: !this.character.previewImage.data && !this.character.previewImage.mime ? null : this.character.previewImage,
                    }
                    this.$emit('submit-requested', payload);
                }
            },
            genericValidator,
            errorValidator,
            onClickConfirmationPopup(value){
                this.showConfirmationPopup = value;     
                this.$router.push({name: "CharactersList" });
            },
        },
        watch:
        {
            putCharacterOutput: function(val)
            {
                if(val)
                {
                    this.showConfirmationPopup = true;
                }
            },
            postCharacterOutput: function(val)
            {
                if(val)
                {
                    this.showConfirmationPopup = true;
                }
            },
            baseCharacter: function()
            {
                this.resetCharacter();
            },
            alertType: function()
            {
                this.submitting = false;
            },
            alertOutput: function()
            {
                this.submitting = false;
    
                // Show pop up in case it is a string error
                // In case it is an array, it is shown on the field
                if(typeof this.alertOutput === 'string' || this.alertOutput instanceof String)
                {
                    alert(this.alertOutput);
                }
            }
        }
      }
    </script> 
     
    <style scoped>
    
        .showDropdown
        {
            display: block !important;
        }
    
        .input-group-append
        {
          height: fit-content;
        }
    
        img
        {
            width: 50%;
            height: 50%;
        }
    
    </style> 